type FirstDocGuidanceInput = {
  isDemo: boolean;
  tasksLoading: boolean;
  tasksError: boolean;
  recentTaskCount?: number;
};

/**
 * Discoverable first-doc chrome (dashboard reopen, edit ? tips).
 * Non-demo with a resolved tasks query. Busy accounts stay included.
 */
export const canShowFirstDocGuidance = ({
  isDemo,
  tasksLoading,
  tasksError,
}: FirstDocGuidanceInput): boolean =>
  !isDemo && !tasksLoading && !tasksError;

/**
 * Auto-open / push only for empty/new accounts (no recent tasks).
 * Busy accounts must not start expanded from localStorage alone.
 */
export const shouldAutoOpenFirstDocGuidance = ({
  isDemo,
  tasksLoading,
  tasksError,
  recentTaskCount = 0,
}: FirstDocGuidanceInput): boolean =>
  canShowFirstDocGuidance({isDemo, tasksLoading, tasksError}) &&
  recentTaskCount === 0;

/** @deprecated Prefer shouldAutoOpenFirstDocGuidance */
export const shouldShowFirstDocGuidance = shouldAutoOpenFirstDocGuidance;

/** createTasks fictional-sample bar: auto-open gate + empty upload queue. */
export const shouldShowCreateTasksSampleGuide = ({
  uploadQueueEmpty,
  isDemo,
  tasksLoading,
  tasksError,
  recentTaskCount,
}: {
  uploadQueueEmpty: boolean;
  isDemo: boolean;
  tasksLoading: boolean;
  tasksError: boolean;
  recentTaskCount: number;
}): boolean => {
  if (!uploadQueueEmpty) return false;
  return shouldAutoOpenFirstDocGuidance({
    isDemo,
    tasksLoading,
    tasksError,
    recentTaskCount,
  });
};

/**
 * Demo visitors with an empty upload queue get the fictional sample as the
 * primary action. Demo accounts have no task list query, so there is nothing
 * else to wait for.
 */
export const shouldShowDemoSampleFirst = ({
  uploadQueueEmpty,
  isDemo,
}: {
  uploadQueueEmpty: boolean;
  isDemo: boolean;
}): boolean => isDemo && uploadQueueEmpty;

/**
 * createTasks post-process success bar (“ready for review”).
 * Same auto-open gate as first-doc guidance (empty/new only).
 * Pass recentTaskCount excluding ids currently in the upload queue so the
 * upload→TASK invalidation race does not hide the bar for first-time users.
 */
export const shouldShowCreateTasksReadyGuide = ({
  hasReadyItem,
  isDemo,
  tasksLoading,
  tasksError,
  recentTaskCount,
}: {
  hasReadyItem: boolean;
  isDemo: boolean;
  tasksLoading: boolean;
  tasksError: boolean;
  recentTaskCount: number;
}): boolean => {
  if (!hasReadyItem) return false;
  return shouldAutoOpenFirstDocGuidance({
    isDemo,
    tasksLoading,
    tasksError,
    recentTaskCount,
  });
};

/** Recent tasks that are not part of the current createTasks upload session. */
export const countOtherRecentTasks = (
  recentTaskIds: string[],
  uploadTaskIds: string[],
): number => {
  const uploadSet = new Set(uploadTaskIds);
  return recentTaskIds.filter((id) => !uploadSet.has(id)).length;
};
